import { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useLanguage } from '@/shared/contexts/LanguageContext';
import { useClients } from '@/features/clients/hooks/useClients';
import {
  useClientMedical, useClientSessions, useClientPhotos,
} from '@/features/clients/hooks/useClientDetail';
import { useSignedContracts, useDeleteSignedContract, useTemplates, useSettings } from '@/shared/hooks/useCloudData';
import { SendConsentDialog } from '@/features/contracts/components/SendConsentDialog';
import { SignaturePad } from '@/features/contracts/components/SignaturePad';
import { generateContractPdf } from '@/features/contracts/services/generatePdf';
import { useToast } from '@/hooks/use-toast';
import { useDrafts } from '@/shared/hooks/useDrafts';
import { useAppointments } from '@/features/appointments/hooks/useAppointments';
import { useInvoices } from '@/features/invoices/hooks/useInvoices';
import { usePayments } from '@/features/payments/hooks/usePayments';
import { useQuotes } from '@/features/quotes/hooks/useQuotes';
import { motion, AnimatePresence } from 'framer-motion';
import { format } from 'date-fns';
import { fr, enUS, es } from 'date-fns/locale';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import {
  ArrowLeft, Pencil, Trash2, Mail, Phone, Calendar, Instagram,
  FileText, MapPin, StickyNote, Plus, Download, Eye,
  User, ClipboardList, HeartPulse, Camera, Image,
  Star, AlertTriangle, Pill, Baby, Zap, Clock,
  CheckCircle2, XCircle, Upload, X, ExternalLink, Share2, FileEdit,
  Play, CalendarPlus, Receipt, FileSignature, Send, Activity,
  TrendingUp, PanelRight, Banknote, Loader2, Check, Link2,
} from 'lucide-react';

const localeMap = { fr, en: enUS, es };

// ─── Types ────────────────────────────────────────────────────────────────────

const TABS = ['activity', 'sessions', 'documents', 'invoices', 'quotes', 'photos'] as const;
type Tab = typeof TABS[number];

type TimelineFilter = 'all' | 'session' | 'payment' | 'contract' | 'appointment';

interface TimelineEvent {
  id: string;
  type: 'session' | 'contract' | 'payment' | 'appointment';
  date: string;
  title: string;
  description: string;
}

// ─── Translations ─────────────────────────────────────────────────────────────

const TRANSLATIONS = {
  fr: {
    back: 'Clients', edit: 'Modifier', delete: 'Supprimer', save: 'Enregistrer', cancel: 'Annuler',
    since: 'Client depuis', contracts: 'Contrats', sessions: 'Sessions', photos: 'Photos',
    notes: 'Notes', profile: 'Profil', health: 'Santé',
    activity: 'Activité', documents: 'Documents', invoices: 'Factures', quotes: 'Devis',
    noContracts: 'Aucun contrat pour ce client', noSessions: 'Aucune session pour ce client',
    noPhotos: 'Aucune photo pour ce client',
    newContract: 'Nouveau contrat', newSession: 'Démarrer session', signedOn: 'Signé le',
    email: 'Email', phone: 'Téléphone', birthDate: 'Date de naissance',
    address: 'Adresse', emergency: 'Contact urgence', vip: 'VIP',
    allergies: 'Allergies', conditions: 'Conditions médicales', medications: 'Médicaments',
    pregnant: 'Enceinte / Allaitante', skinReactions: 'Réactions cutanées',
    painTolerance: 'Tolérance à la douleur', low: 'Faible', high: 'Élevée',
    sessionStatusMap: { in_progress: 'En cours', completed: 'Terminée', cancelled: 'Annulée' } as Record<string, string>,
    deleteConfirm: 'Archiver ce client ?',
    deleteDesc: 'Le client sera masqué de la liste mais ses données (sessions, contrats, photos) seront conservées.',
    uploadPhoto: 'Ajouter une photo', photoCaption: 'Légende (optionnel)',
    photoType: 'Type', tattoo: 'Tatouage', reference: 'Référence', profilePic: 'Photo de profil',
    clientNotFound: 'Client introuvable', saving: 'Sauvegarde...', saved: 'Sauvegardé',
    editProfile: 'Modifier le profil', name: 'Nom complet',
    referral: 'Source', referralSource: 'Comment a-t-il connu le studio ?',
    stylePrefs: 'Styles préférés', addStyle: 'Ajouter un style',
    emergencyName: 'Nom du contact', emergencyPhone: 'Téléphone urgence',
  },
  en: {
    back: 'Clients', edit: 'Edit', delete: 'Delete', save: 'Save', cancel: 'Cancel',
    since: 'Client since', contracts: 'Contracts', sessions: 'Sessions', photos: 'Photos',
    notes: 'Notes', profile: 'Profile', health: 'Health',
    activity: 'Activity', documents: 'Documents', invoices: 'Invoices', quotes: 'Quotes',
    noContracts: 'No contracts for this client', noSessions: 'No sessions for this client',
    noPhotos: 'No photos for this client',
    newContract: 'New contract', newSession: 'Start session', signedOn: 'Signed on',
    email: 'Email', phone: 'Phone', birthDate: 'Date of birth',
    address: 'Address', emergency: 'Emergency contact', vip: 'VIP',
    allergies: 'Allergies', conditions: 'Medical conditions', medications: 'Medications',
    pregnant: 'Pregnant / Breastfeeding', skinReactions: 'Skin reactions',
    painTolerance: 'Pain tolerance', low: 'Low', high: 'High',
    sessionStatusMap: { in_progress: 'In progress', completed: 'Completed', cancelled: 'Cancelled' } as Record<string, string>,
    deleteConfirm: 'Archive this client?',
    deleteDesc: 'The client will be hidden from the list but their data (sessions, contracts, photos) will be kept.',
    uploadPhoto: 'Add photo', photoCaption: 'Caption (optional)',
    photoType: 'Type', tattoo: 'Tattoo', reference: 'Reference', profilePic: 'Profile picture',
    clientNotFound: 'Client not found', saving: 'Saving...', saved: 'Saved',
    editProfile: 'Edit profile', name: 'Full name',
    referral: 'Source', referralSource: 'How did they find the studio?',
    stylePrefs: 'Preferred styles', addStyle: 'Add style',
    emergencyName: 'Contact name', emergencyPhone: 'Emergency phone',
  },
  es: {
    back: 'Clientes', edit: 'Editar', delete: 'Eliminar', save: 'Guardar', cancel: 'Cancelar',
    since: 'Cliente desde', contracts: 'Contratos', sessions: 'Sesiones', photos: 'Fotos',
    notes: 'Notas', profile: 'Perfil', health: 'Salud',
    activity: 'Actividad', documents: 'Documentos', invoices: 'Facturas', quotes: 'Presupuestos',
    noContracts: 'Sin contratos para este cliente', noSessions: 'Sin sesiones para este cliente',
    noPhotos: 'Sin fotos para este cliente',
    newContract: 'Nuevo contrato', newSession: 'Iniciar sesión', signedOn: 'Firmado el',
    email: 'Correo', phone: 'Teléfono', birthDate: 'Fecha de nacimiento',
    address: 'Dirección', emergency: 'Contacto de emergencia', vip: 'VIP',
    allergies: 'Alergias', conditions: 'Condiciones médicas', medications: 'Medicamentos',
    pregnant: 'Embarazada / Lactando', skinReactions: 'Reacciones cutáneas',
    painTolerance: 'Tolerancia al dolor', low: 'Baja', high: 'Alta',
    sessionStatusMap: { in_progress: 'En curso', completed: 'Completada', cancelled: 'Cancelada' } as Record<string, string>,
    deleteConfirm: '¿Archivar este cliente?',
    deleteDesc: 'El cliente se ocultará de la lista pero sus datos se conservarán.',
    uploadPhoto: 'Agregar foto', photoCaption: 'Pie de foto (opcional)',
    photoType: 'Tipo', tattoo: 'Tatuaje', reference: 'Referencia', profilePic: 'Foto de perfil',
    clientNotFound: 'Cliente no encontrado', saving: 'Guardando...', saved: 'Guardado',
    editProfile: 'Editar perfil', name: 'Nombre completo',
    referral: 'Fuente', referralSource: '¿Cómo conoció el estudio?',
    stylePrefs: 'Estilos preferidos', addStyle: 'Agregar estilo',
    emergencyName: 'Nombre del contacto', emergencyPhone: 'Teléfono de emergencia',
  },
};

const TAB_ICONS: Record<Tab, React.ReactNode> = {
  activity:  <Activity size={14} />,
  sessions:  <Clock size={14} />,
  documents: <ClipboardList size={14} />,
  invoices:  <Receipt size={14} />,
  quotes:    <FileSignature size={14} />,
  photos:    <Camera size={14} />,
};

// ─── Sub-components ───────────────────────────────────────────────────────────

function StatusBadge({ status, map }: { status: string; map: Record<string, string> }) {
  const styles: Record<string, string> = {
    in_progress: 'text-warning bg-warning/10 border-warning/20',
    completed: 'text-primary bg-primary/10 border-primary/20',
    cancelled: 'text-destructive bg-destructive/10 border-destructive/20',
  };
  const icons: Record<string, React.ReactNode> = {
    in_progress: <Clock size={11} />,
    completed: <CheckCircle2 size={11} />,
    cancelled: <XCircle size={11} />,
  };
  return (
    <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border font-medium ${styles[status] ?? styles.in_progress}`}>
      {icons[status]} {map[status] ?? status}
    </span>
  );
}

function PainDots({ value, onChange }: { value: number | null; onChange: (v: number) => void }) {
  return (
    <div className="flex gap-2">
      {[1, 2, 3, 4, 5].map(n => (
        <button key={n} onClick={() => onChange(n)}
          className={`w-8 h-8 rounded-full border-2 transition-all text-xs font-bold
            ${(value ?? 0) >= n ? 'bg-primary border-primary text-primary-foreground' : 'border-border/50 text-muted-foreground hover:border-primary/50'}`}>
          {n}
        </button>
      ))}
    </div>
  );
}

function TagInput({ tags, onChange, placeholder }: { tags: string[]; onChange: (t: string[]) => void; placeholder: string }) {
  const [val, setVal] = useState('');
  const add = () => {
    const v = val.trim();
    if (v && !tags.includes(v)) onChange([...tags, v]);
    setVal('');
  };
  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <Input value={val} onChange={e => setVal(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), add())}
          placeholder={placeholder} className="bg-secondary/50 border-border/50 h-9 text-sm" />
        <Button type="button" variant="outline" size="sm" onClick={add} className="border-border/50 h-9"><Plus size={14} /></Button>
      </div>
      {tags.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {tags.map(tag => (
            <span key={tag} className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-primary/10 text-primary border border-primary/20 font-medium">
              {tag}
              <button onClick={() => onChange(tags.filter(t => t !== tag))} className="hover:text-destructive ml-0.5"><X size={10} /></button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function InfoRow({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="flex items-start gap-2.5">
      <span className="text-primary/50 mt-0.5 shrink-0">{icon}</span>
      <div>
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="text-sm text-foreground">{value}</p>
      </div>
    </div>
  );
}

function FormField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}

function HealthSection({ icon, title, children }: { icon: React.ReactNode; title: string; children: React.ReactNode }) {
  return (
    <div className="glass-card p-4 space-y-2.5">
      <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">{icon} {title}</h4>
      {children}
    </div>
  );
}

function EmptyState({ icon, text, children }: { icon: React.ReactNode; text: string; children?: React.ReactNode }) {
  return (
    <div className="glass-card p-12 text-center text-muted-foreground space-y-2">
      <div className="opacity-20 mx-auto w-fit">{icon}</div>
      <p className="text-sm">{text}</p>
      {children}
    </div>
  );
}

const STYLE_LIST = [
  'Old School', 'Japonais', 'Réaliste', 'Fine Line', 'Géométrique',
  'Blackwork', 'Neo-trad', 'Aquarelle', 'Dotwork', 'Tribal', 'Minimaliste',
];

const TIMELINE_DOT: Record<string, string> = {
  session:     'bg-primary',
  contract:    'bg-blue-500',
  payment:     'bg-green-500',
  appointment: 'bg-amber-500',
};

const TIMELINE_ICON: Record<string, React.ReactNode> = {
  session:     <Play size={10} className="text-white" />,
  contract:    <FileText size={10} className="text-white" />,
  payment:     <Banknote size={10} className="text-white" />,
  appointment: <CalendarPlus size={10} className="text-white" />,
};

// ─── Main ─────────────────────────────────────────────────────────────────────

export default function ClientDetail() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { clients, updateClient, archiveClient } = useClients();
  const { toast } = useToast();
  const t = TRANSLATIONS[language];
  const locale = localeMap[language];

  // ── Existing state ──────────────────────────────────────────────────────────
  const [contractFilter, setContractFilter] = useState<'all' | 'signed' | 'drafts'>('all');
  const { data: clientDraftsData = [] } = useDrafts(id);
  const clientDrafts = clientDraftsData;
  const [activeTab, setActiveTab] = useState<Tab>('activity');
  const [editMode, setEditMode] = useState(false);
  const [saving,   setSaving]  = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [photoCaption, setPhotoCaption] = useState('');
  const [photoType, setPhotoType] = useState<'tattoo' | 'reference' | 'profile'>('tattoo');
  const [filterType, setFilterType] = useState<'all' | 'tattoo' | 'reference' | 'profile'>('all');
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [deleteContractId, setDeleteContractId] = useState<string | null>(null);
  const [showSendConsent, setShowSendConsent] = useState(false);
  const [counterSignId, setCounterSignId] = useState<string | null>(null);
  const [practitionerSig, setPractitionerSig] = useState('');
  const [counterSigning, setCounterSigning] = useState(false);
  const queryClient = useQueryClient();

  // ── New state ───────────────────────────────────────────────────────────────
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [timelineFilter, setTimelineFilter] = useState<TimelineFilter>('all');
  const [showSessionBanner, setShowSessionBanner] = useState(false);

  // ── Existing hooks ──────────────────────────────────────────────────────────
  const client = clients.find(c => c.id === id);
  const clientExtra = client as any;
  const { medical, upsertMedical } = useClientMedical(id);
  const { data: allContracts = [] } = useSignedContracts();
  const { data: templates = [] } = useTemplates();
  const { data: settings } = useSettings();
  const deleteContract = useDeleteSignedContract();
  const contracts = allContracts.filter(c => (c as any).clientId === id);
  const { data: sessions = [], isLoading: sessionsLoading } = useClientSessions(id);
  const { photos, isLoading: photosLoading, uploadPhoto, deletePhoto } = useClientPhotos(id);

  // ── New hooks ───────────────────────────────────────────────────────────────
  const { data: allAppts = [] } = useAppointments();
  const clientAppts = allAppts.filter(a => a.client_id === id);
  const todayIso = new Date().toISOString().split('T')[0];
  const nextAppt = clientAppts
    .filter(a => a.date >= todayIso && a.status !== 'cancelled')
    .sort((a, b) => a.date.localeCompare(b.date) || a.start_time.localeCompare(b.start_time))[0] ?? null;

  const { data: allInvoices = [] } = useInvoices();
  const clientInvoices = allInvoices.filter(inv => inv.client_id === id);

  const { data: allPayments = [] } = usePayments();
  const clientPayments = allPayments.filter(p => p.client_id === id);

  const { data: allQuotes = [] } = useQuotes();
  const clientQuotes = allQuotes.filter(q => q.client_id === id);

  // ── Pending counter-sign contracts (direct Supabase query, real-time) ────────
  const { data: pendingContracts = [], refetch: refetchPending } = useQuery({
    queryKey: ['pending-contracts', id],
    enabled: !!id,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from('signed_contracts')
        .select('id, template_name, signed_at, client_name')
        .eq('client_id', id)
        .eq('status', 'pending_practitioner')
        .order('signed_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as { id: string; template_name: string; signed_at: string; client_name: string }[];
    },
  });

  const blobToBase64 = async (blob: Blob): Promise<string> => {
    const buffer = await blob.arrayBuffer();
    const bytes = new Uint8Array(buffer);
    const chunkSize = 0x8000;
    let binary = '';
    for (let i = 0; i < bytes.length; i += chunkSize) {
      binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
    }
    return btoa(binary);
  };

  const handleCounterSign = async () => {
    if (!counterSignId || !practitionerSig) return;
    setCounterSigning(true);
    try {
      const { error } = await (supabase as any)
        .from('signed_contracts')
        .update({ status: 'completed', practitioner_signature_data_url: practitionerSig })
        .eq('id', counterSignId);
      if (error) throw error;

      // Fetch full contract + template for PDF generation
      const { data: fullContract } = await (supabase as any)
        .from('signed_contracts')
        .select('*')
        .eq('id', counterSignId)
        .single();

      const { data: fullTemplate } = await (supabase as any)
        .from('templates')
        .select('*')
        .eq('id', fullContract?.template_id)
        .single();

      if (fullContract && fullTemplate) {
        const contractForPdf = {
          id: fullContract.id,
          templateId: fullContract.template_id,
          templateName: fullContract.template_name,
          clientId: fullContract.client_id,
          clientName: fullContract.client_name,
          clientEmail: fullContract.client_email ?? '',
          language: fullContract.language ?? 'fr',
          fieldValues: fullContract.field_values ?? {},
          checkboxValues: fullContract.checkbox_values ?? {},
          signatureDataUrl: fullContract.signature_data_url ?? '',
          signedAt: fullContract.signed_at,
          practitionerEmail: settings?.practitionerEmail ?? '',
          practitionerSignatureDataUrl: practitionerSig,
        };

        try {
          const pdfBlob = generateContractPdf(contractForPdf, fullTemplate, 'blob', {
            logoDataUrl: settings?.logoUrl,
            businessName: settings?.businessName || settings?.practitionerName,
          }) as Blob;

          if (pdfBlob) {
            const pdfBase64 = await blobToBase64(pdfBlob);
            const { data: { session: authSession } } = await supabase.auth.getSession();
            await supabase.functions.invoke('send-contract-email', {
              headers: { Authorization: `Bearer ${authSession?.access_token}` },
              body: {
                clientEmail: fullContract.client_email || null,
                practitionerEmail: settings?.practitionerEmail || null,
                clientName: fullContract.client_name,
                practitionerName: settings?.practitionerName,
                templateName: fullContract.template_name,
                signedAt: new Date().toISOString(),
                pdfBase64,
              },
            });
          }
        } catch (pdfErr) {
          console.error('[handleCounterSign] PDF/email error:', pdfErr);
        }
      }

      setCounterSignId(null);
      setPractitionerSig('');
      refetchPending();
      queryClient.invalidateQueries({ queryKey: ['pending-cosign'] });
      queryClient.invalidateQueries({ queryKey: ['pending-contracts', id] });
      queryClient.invalidateQueries({ queryKey: ['signed_contracts'] });
    } catch (err: any) {
      toast({ title: 'Erreur', description: err?.message, variant: 'destructive' });
    } finally {
      setCounterSigning(false);
    }
  };

  // ── Financial stats ─────────────────────────────────────────────────────────
  const totalInvoiced = clientInvoices.reduce((s, inv) => s + (inv.total_ttc ?? 0), 0);
  const totalPaid = clientPayments
    .filter(p => p.status === 'paid')
    .reduce((s, p) => s + Number(p.amount), 0);
  const balance = totalPaid - totalInvoiced; // negative = client still owes

  // ── Timeline events ─────────────────────────────────────────────────────────
  const allTimelineEvents: TimelineEvent[] = [
    ...sessions.map(s => ({
      id: s.id,
      type: 'session' as const,
      date: s.started_at || '',
      title: s.project_name || 'Session',
      description: t.sessionStatusMap[s.status] ?? s.status,
    })),
    ...contracts.map(c => {
      const ca = c as any;
      return {
        id: ca.id as string,
        type: 'contract' as const,
        date: (ca.signedAt || ca.created_at || '') as string,
        title: (ca.templateName || 'Contrat') as string,
        description: 'Contrat signé',
      };
    }),
    ...clientPayments.map(p => ({
      id: p.id,
      type: 'payment' as const,
      date: p.payment_date,
      title: `${Number(p.amount).toFixed(2)} €`,
      description: p.description || p.client_name,
    })),
    ...clientAppts.map(a => ({
      id: a.id,
      type: 'appointment' as const,
      date: a.date,
      title: a.project_name || 'Rendez-vous',
      description: a.start_time?.slice(0, 5) || '',
    })),
  ]
    .filter(e => !!e.date)
    .sort((a, b) => b.date.localeCompare(a.date));

  const timelineEvents = timelineFilter === 'all'
    ? allTimelineEvents
    : allTimelineEvents.filter(e => e.type === timelineFilter);

  // ── Edit form state ─────────────────────────────────────────────────────────
  const [editForm, setEditForm] = useState({
    first_name: '', last_name: '', email: '', phone: '', instagram: '', birth_date: '',
    addr_street: '', addr_zip: '', addr_city: '',
    referral_source: '', is_vip: false,
    ec_name: '', ec_phone: '',
    style_preferences: [] as string[],
    notes: '',
    allergies: [] as string[], conditions: [] as string[],
    medications: '', is_pregnant: false, skin_reactions: '', pain_tolerance: null as number | null,
  });

  const [notesDraft, setNotesDraft] = useState(client?.notes || '');

  // ── URL param effects ───────────────────────────────────────────────────────
  useEffect(() => {
    const tab = searchParams.get('tab') as Tab | null;
    if (tab && TABS.includes(tab as Tab)) setActiveTab(tab as Tab);
    if (searchParams.get('edit') === 'true' && client) {
      initEditForm();
      setEditMode(true);
      navigate(`/clients/${id}`, { replace: true });
    }
    if (searchParams.get('sessionCreated') === 'true') {
      setShowSessionBanner(true);
      navigate(`/clients/${id}`, { replace: true });
      const timer = setTimeout(() => setShowSessionBanner(false), 3000);
      return () => clearTimeout(timer);
    }
    const cosign = searchParams.get('cosign');
    if (cosign) {
      setCounterSignId(cosign);
      setPractitionerSig('');
      navigate(`/clients/${id}?tab=documents`, { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client?.id, searchParams.get('edit'), searchParams.get('tab'), searchParams.get('sessionCreated'), searchParams.get('cosign')]);

  // ── Handlers ────────────────────────────────────────────────────────────────
  const initEditForm = () => {
    if (!client) return;
    const addr = (client.address as Record<string, string>) || {};
    const ec   = (client.emergency_contact as Record<string, string>) || {};
    setEditForm({
      first_name: client.first_name || '', last_name: client.last_name || '',
      email: client.email || '', phone: client.phone || '',
      instagram: (client as any).instagram || '', birth_date: client.birth_date || '',
      addr_street: addr.street || '', addr_zip: addr.postal_code || '', addr_city: addr.city || '',
      referral_source: (client as any).referral_source || '',
      is_vip: (client as any).is_vip || false,
      ec_name: ec.name || '', ec_phone: ec.phone || '',
      style_preferences: (client as any).style_preferences || [],
      notes: client.notes || '',
      allergies: medical?.allergies || [], conditions: medical?.conditions || [],
      medications: medical?.medications || '', is_pregnant: medical?.is_pregnant || false,
      skin_reactions: medical?.skin_reactions || '', pain_tolerance: medical?.pain_tolerance ?? null,
    });
  };

  const handleSaveEdit = async () => {
    if (!client) return;
    setSaving(true);
    try {
      await updateClient.mutateAsync({
        id: client.id,
        first_name: editForm.first_name, last_name: editForm.last_name,
        email: editForm.email || undefined, phone: editForm.phone || undefined,
        birth_date: editForm.birth_date || undefined, notes: editForm.notes,
        instagram: editForm.instagram || undefined,
        referral_source: editForm.referral_source || null, is_vip: editForm.is_vip,
        emergency_contact: { name: editForm.ec_name, phone: editForm.ec_phone },
        style_preferences: editForm.style_preferences,
      });
      await upsertMedical.mutateAsync({
        client_id: client.id,
        allergies: editForm.allergies, conditions: editForm.conditions,
        medications: editForm.medications, is_pregnant: editForm.is_pregnant,
        skin_reactions: editForm.skin_reactions, pain_tolerance: editForm.pain_tolerance,
      });
      setEditMode(false);
      toast({ title: 'Profil mis à jour ✓' });
    } catch {
      toast({ title: 'Erreur lors de la sauvegarde', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleArchiveClient = async () => {
    if (!client) return;
    try {
      await archiveClient.mutateAsync(client.id);
      navigate('/clients');
      toast({ title: 'Client archivé ✓' });
    } catch (err: any) {
      toast({ title: 'Erreur', description: err?.message, variant: 'destructive' });
    }
  };

  const handlePreviewPdf = (contractId: string) => {
    const contract = allContracts.find(c => c.id === contractId);
    if (!contract) return;
    const template = templates.find(t => t.id === contract.templateId);
    const blob = generateContractPdf(contract, template, 'blob') as Blob;
    if (blob) {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(URL.createObjectURL(blob));
    }
  };

  const closePreview = useCallback(() => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
  }, [previewUrl]);

  const handleExportPdf = (contractId: string) => {
    const contract = allContracts.find(c => c.id === contractId);
    if (!contract) return;
    const template = templates.find(t => t.id === contract.templateId);
    generateContractPdf(contract, template, 'save');
  };

  const handleShare = async (contractId: string) => {
    const contract = allContracts.find(c => c.id === contractId);
    if (!contract) return;
    const template = templates.find(t => t.id === contract.templateId);
    const blob = generateContractPdf(contract, template, 'blob') as Blob;
    if (!blob) return;
    const fileName = `${contract.clientName || 'contract'}_${new Date(contract.signedAt).toISOString().slice(0, 10)}.pdf`;
    const file = new File([blob], fileName, { type: 'application/pdf' });
    try {
      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({ title: contract.templateName, files: [file] });
        return;
      }
      generateContractPdf(contract, template, 'save');
    } catch (err: unknown) {
      if (err instanceof Error && err.name === 'AbortError') return;
    }
  };

  const confirmDeleteContract = () => {
    if (!deleteContractId) return;
    deleteContract.mutate(deleteContractId, {
      onSuccess: () => { setDeleteContractId(null); toast({ title: t.delete, description: '✓' }); },
    });
  };

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingPhoto(true);
    try {
      await uploadPhoto.mutateAsync({ file, type: photoType, caption: photoCaption });
      setPhotoCaption('');
      toast({ title: t.saved, description: '✓' });
    } catch { toast({ title: 'Error', variant: 'destructive' }); }
    finally { setUploadingPhoto(false); if (fileInputRef.current) fileInputRef.current.value = ''; }
  };

  // ── Early return ────────────────────────────────────────────────────────────
  if (!client) {
    return (
      <div className="p-6 max-w-5xl mx-auto">
        <Button variant="ghost" onClick={() => navigate('/clients')} className="gap-2 mb-6">
          <ArrowLeft size={16} /> {t.back}
        </Button>
        <p className="text-muted-foreground">{t.clientNotFound}</p>
      </div>
    );
  }

  const initials = client.name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
  const addressObj = (client.address as Record<string, string>) || {};
  const profilePhoto = photos.find(p => p.type === 'profile');
  const hasAllergies = (medical?.allergies?.length ?? 0) > 0;
  const hasContract = contracts.length > 0;
  const hasActiveSession = sessions.some(s => s.status === 'in_progress');

  // ── Sidebar content (shared between desktop and mobile) ─────────────────────
  const SidebarContent = () => (
    <div className="space-y-4">
      {/* Prochain RDV */}
      <div className="glass-card p-4 space-y-2">
        <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
          <CalendarPlus size={12} /> Prochain RDV
        </h4>
        {nextAppt ? (
          <div>
            <p className="text-sm font-medium text-foreground">
              {format(new Date(nextAppt.date), 'dd MMM yyyy', { locale })}
            </p>
            <p className="text-xs text-muted-foreground">{nextAppt.start_time?.slice(0, 5)} · {nextAppt.project_name || '—'}</p>
            <p className="text-xs text-muted-foreground mt-1">
              {nextAppt.is_paid
                ? <span className="text-green-600">Acompte versé</span>
                : <span className="text-amber-500">Acompte en attente</span>}
            </p>
          </div>
        ) : (
          <p className="text-xs text-muted-foreground italic">Aucun RDV à venir</p>
        )}
      </div>

      {/* Informations médicales */}
      <div className="glass-card p-4 space-y-2">
        <div className="flex items-center justify-between">
          <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
            <HeartPulse size={12} /> Santé
          </h4>
          <button
            onClick={() => { initMedical(); setEditingHealth(true); }}
            className="text-xs text-muted-foreground hover:text-foreground"
          >
            <Pencil size={11} />
          </button>
        </div>
        {medical ? (
          <div className="space-y-1.5">
            {hasAllergies && (
              <div>
                <p className="text-xs text-destructive font-medium">⚠ Allergies</p>
                <div className="flex flex-wrap gap-1 mt-1">
                  {medical.allergies.map(a => (
                    <span key={a} className="text-xs px-1.5 py-0.5 rounded bg-destructive/10 text-destructive">{a}</span>
                  ))}
                </div>
              </div>
            )}
            {client.birth_date && (
              <p className="text-xs text-muted-foreground flex items-center gap-1">
                <Baby size={10} /> {format(new Date(client.birth_date), 'dd/MM/yyyy')}
              </p>
            )}
            {medical.pain_tolerance && (
              <p className="text-xs text-muted-foreground">Douleur : {medical.pain_tolerance}/5</p>
            )}
            {medical.is_pregnant && (
              <p className="text-xs text-amber-600 font-medium">Enceinte / Allaitante</p>
            )}
          </div>
        ) : (
          <p className="text-xs text-muted-foreground italic">Aucune donnée médicale</p>
        )}
      </div>

      {/* Finances */}
      <div className="glass-card p-4 space-y-2">
        <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
          <TrendingUp size={12} /> Finances
        </h4>
        <div className="space-y-1">
          <div className="flex justify-between text-xs">
            <span className="text-muted-foreground">Facturé</span>
            <span className="tabular-nums font-medium">{totalInvoiced.toFixed(2)} €</span>
          </div>
          <div className="flex justify-between text-xs">
            <span className="text-muted-foreground">Encaissé</span>
            <span className="tabular-nums font-medium text-green-600">{totalPaid.toFixed(2)} €</span>
          </div>
          <div className="flex justify-between text-xs border-t border-border/40 pt-1 mt-1">
            <span className="text-muted-foreground">Balance</span>
            <span className={`tabular-nums font-bold ${balance < 0 ? 'text-amber-500' : 'text-foreground'}`}>
              {balance >= 0 ? '+' : ''}{balance.toFixed(2)} €
            </span>
          </div>
        </div>
      </div>

      {/* Notes privées */}
      <div className="glass-card p-4 space-y-2">
        <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
          <StickyNote size={12} /> Notes équipe
        </h4>
        <Textarea
          value={notesDraft}
          onChange={e => setNotesDraft(e.target.value)}
          onBlur={() => {
            if (notesDraft !== (client.notes || '')) {
              updateClient.mutate({ id: client.id, notes: notesDraft });
            }
          }}
          placeholder="Notes internes équipe…"
          className="text-xs bg-secondary/30 border-border/50 resize-none"
          rows={3}
        />
      </div>

      {/* Références visuelles */}
      {photos.filter(p => p.type === 'reference').length > 0 && (
        <div className="glass-card p-4 space-y-2">
          <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
            <Image size={12} /> Références
          </h4>
          <div className="grid grid-cols-3 gap-1">
            {photos.filter(p => p.type === 'reference').slice(0, 6).map(photo => (
              <a key={photo.id} href={photo.url} target="_blank" rel="noreferrer">
                <img src={photo.url} alt={photo.caption} className="w-full aspect-square object-cover rounded-lg" />
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="pb-10">
      {/* ── Sticky top bar ────────────────────────────────────────────────── */}
      <div className="sticky top-0 z-20 bg-background/80 backdrop-blur-xl border-b border-border/40 px-6 py-3">
        <div className="max-w-6xl mx-auto flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/clients')}
            className="h-9 w-9 rounded-xl bg-secondary/60 hover:bg-secondary shrink-0">
            <ArrowLeft size={18} />
          </Button>
          <div className="flex-1" />
          <div className="flex gap-2 shrink-0">
            <Button variant="ghost" size="icon" className="h-9 w-9 rounded-xl lg:hidden"
              onClick={() => setSidebarOpen(v => !v)}>
              <PanelRight size={18} />
            </Button>
            <Button variant="outline" size="sm" onClick={() => { initEditForm(); setEditMode(true); }}
              className="gap-1.5 h-8 border-border/50 text-xs">
              <Pencil size={13} /> Modifier le profil
            </Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" size="sm"
                  className="gap-1.5 h-8 text-xs text-destructive hover:text-destructive border-destructive/30 hover:border-destructive">
                  <Trash2 size={13} />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent className="glass-card border-border/50">
                <AlertDialogHeader>
                  <AlertDialogTitle>{t.deleteConfirm}</AlertDialogTitle>
                  <AlertDialogDescription className="text-muted-foreground">{t.deleteDesc}</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel className="border-border/50">{t.cancel}</AlertDialogCancel>
                  <AlertDialogAction onClick={handleArchiveClient}>{t.delete}</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
      </div>

      {showSessionBanner && (
        <div className="bg-green-500 text-white text-sm font-semibold text-center py-2 px-4 flex items-center justify-center gap-2">
          <CheckCircle2 size={16} /> Session créée avec succès
        </div>
      )}

      <div className="max-w-6xl mx-auto px-6 pt-6 space-y-4">

        {/* ── STEP 1: Hero — 3 lignes ─────────────────────────────────────── */}
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
          className="glass-card p-5 space-y-3">

          {/* Ligne 1 : avatar + nom + tags */}
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center text-primary font-bold text-xl shrink-0 overflow-hidden">
              {profilePhoto?.url
                ? <img src={profilePhoto.url} alt={client.name} className="w-full h-full object-cover" />
                : initials}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-xl font-bold font-display text-foreground">{client.name}</h1>
                {clientExtra?.is_vip && (
                  <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-warning/10 text-warning border border-warning/20 font-medium">
                    <Star size={10} className="fill-warning" /> VIP
                  </span>
                )}
                {hasAllergies && (
                  <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-destructive/10 text-destructive border border-destructive/20 font-medium">
                    <AlertTriangle size={10} /> Allergie
                  </span>
                )}
                {hasContract ? (
                  <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-green-500/10 text-green-600 border border-green-500/20 font-medium">
                    <CheckCircle2 size={10} /> Consentement OK
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-600 border border-amber-500/20 font-medium">
                    <XCircle size={10} /> Sans consentement
                  </span>
                )}
                {hasActiveSession && (
                  <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/20 font-medium">
                    <Play size={10} /> Session en cours
                  </span>
                )}
              </div>

              {/* Ligne 2 : contacts actionnables */}
              <div className="flex flex-wrap gap-3 mt-1.5">
                {client.phone && (
                  <a href={`tel:${client.phone}`} className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 transition-colors">
                    <Phone size={11} className="text-primary/50" />{client.phone}
                  </a>
                )}
                {client.email && (
                  <a href={`mailto:${client.email}`} className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 transition-colors">
                    <Mail size={11} className="text-primary/50" />{client.email}
                  </a>
                )}
                {client.instagram && (
                  <a href={`https://instagram.com/${client.instagram.replace('@', '')}`}
                    target="_blank" rel="noreferrer"
                    className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 transition-colors">
                    <Instagram size={11} className="text-primary/50" />
                    @{client.instagram.replace('@', '')}
                  </a>
                )}
                {addressObj.city && (
                  <span className="text-xs text-muted-foreground flex items-center gap-1">
                    <MapPin size={11} className="text-primary/50" />{addressObj.city}
                  </span>
                )}
                <p className="text-xs text-muted-foreground flex items-center gap-1">
                  <Calendar size={11} className="text-primary/50" />
                  {t.since} {format(new Date(client.created_at), 'MMMM yyyy', { locale })}
                </p>
              </div>
            </div>
          </div>

          {/* Styles préférés — badges lecture */}
          {(client.style_preferences || []).length > 0 && (
            <div className="flex flex-wrap gap-1 px-1">
              {(client.style_preferences || []).map(s => (
                <span key={s} className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/20 font-medium">{s}</span>
              ))}
            </div>
          )}

          {/* Ligne 3 : 4 stats */}
          <div className="grid grid-cols-4 gap-2 pt-2 border-t border-border/40">
            <div className="text-center">
              <p className={`text-lg font-bold tabular-nums ${balance < 0 ? 'text-amber-500' : 'text-foreground'}`}>
                {balance >= 0 ? '+' : ''}{balance.toFixed(0)} €
              </p>
              <p className="text-xs text-muted-foreground">Balance</p>
            </div>
            <div className="text-center">
              <p className="text-lg font-bold text-green-600 tabular-nums">{totalPaid.toFixed(0)} €</p>
              <p className="text-xs text-muted-foreground">Encaissé</p>
            </div>
            <div className="text-center">
              <p className="text-lg font-bold text-primary tabular-nums">
                {nextAppt ? format(new Date(nextAppt.date), 'dd/MM') : '—'}
              </p>
              <p className="text-xs text-muted-foreground">Prochain RDV</p>
            </div>
            <div className="text-center">
              <p className="text-lg font-bold text-foreground tabular-nums">{sessions.length}</p>
              <p className="text-xs text-muted-foreground">Sessions</p>
            </div>
          </div>
        </motion.div>

        {/* ── STEP 2: Quick actions bar ──────────────────────────────────── */}
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.05 }}
          className="flex gap-2 flex-wrap">
          <Button
            className="gap-2 bg-green-600 hover:bg-green-700 text-white"
            onClick={() => navigate(`/sessions/new?clientId=${client.id}`)}>
            <Play size={14} /> Démarrer session
          </Button>
          <Button variant="outline" size="sm" className="gap-1.5 border-border/50"
            onClick={() => navigate(`/appointments?clientId=${client.id}`)}>
            <CalendarPlus size={14} /> RDV
          </Button>
          <Button variant="outline" size="sm" className="gap-1.5 border-border/50"
            onClick={() => navigate(`/quotes/new?clientId=${client.id}`)}>
            <FileSignature size={14} /> Devis
          </Button>
          <Button variant="outline" size="sm" className="gap-1.5 border-border/50"
            onClick={() => navigate(`/payments?clientId=${client.id}`)}>
            <Receipt size={14} /> Encaisser
          </Button>
          <Button variant="outline" size="sm" className="gap-1.5 border-border/50"
            onClick={() => { initEditForm(); setEditMode(true); }}>
            <StickyNote size={14} /> Note
          </Button>
          <Button variant="outline" size="sm" className="gap-1.5 border-border/50"
            onClick={() => navigate(`/templates?client_id=${client.id}&mode=select`)}>
            <Send size={14} /> Consentement
          </Button>
        </motion.div>


        {/* ── Edit mode inline ─────────────────────────────────────────── */}
        {editMode ? (
          <div className="space-y-4 pb-10">

            {/* Identité */}
            <div className="glass-card p-5 space-y-3">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Identité</p>
              <div className="grid grid-cols-2 gap-3">
                <FormField label="Prénom *">
                  <Input value={editForm.first_name} onChange={e => setEditForm(f => ({ ...f, first_name: e.target.value }))} className="bg-secondary/50 border-border/50" />
                </FormField>
                <FormField label="Nom">
                  <Input value={editForm.last_name} onChange={e => setEditForm(f => ({ ...f, last_name: e.target.value }))} className="bg-secondary/50 border-border/50" />
                </FormField>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <FormField label="Email">
                  <Input type="email" value={editForm.email} onChange={e => setEditForm(f => ({ ...f, email: e.target.value }))} className="bg-secondary/50 border-border/50" />
                </FormField>
                <FormField label="Téléphone">
                  <Input type="tel" value={editForm.phone} onChange={e => setEditForm(f => ({ ...f, phone: e.target.value }))} className="bg-secondary/50 border-border/50" />
                </FormField>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <FormField label="Instagram">
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">@</span>
                    <Input value={editForm.instagram} onChange={e => setEditForm(f => ({ ...f, instagram: e.target.value }))}
                      placeholder="votre.compte" className="bg-secondary/50 border-border/50 pl-7" />
                  </div>
                </FormField>
                <FormField label="Date de naissance">
                  <Input type="date" value={editForm.birth_date} onChange={e => setEditForm(f => ({ ...f, birth_date: e.target.value }))} className="bg-secondary/50 border-border/50" />
                </FormField>
              </div>
            </div>

            {/* Adresse */}
            <div className="glass-card p-5 space-y-3">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Adresse</p>
              <FormField label="Rue">
                <Input value={editForm.addr_street} onChange={e => setEditForm(f => ({ ...f, addr_street: e.target.value }))} placeholder="12 rue de la Paix" className="bg-secondary/50 border-border/50" />
              </FormField>
              <div className="grid grid-cols-[1fr_2fr] gap-3">
                <FormField label="Code postal">
                  <Input value={editForm.addr_zip} onChange={e => setEditForm(f => ({ ...f, addr_zip: e.target.value }))} placeholder="75001" className="bg-secondary/50 border-border/50" />
                </FormField>
                <FormField label="Ville">
                  <Input value={editForm.addr_city} onChange={e => setEditForm(f => ({ ...f, addr_city: e.target.value }))} placeholder="Paris" className="bg-secondary/50 border-border/50" />
                </FormField>
              </div>
            </div>

            {/* Studio */}
            <div className="glass-card p-5 space-y-3">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Studio</p>
              <FormField label="Comment a-t-il connu le studio ?">
                <Input value={editForm.referral_source} onChange={e => setEditForm(f => ({ ...f, referral_source: e.target.value }))}
                  placeholder="Instagram, bouche à oreille…" className="bg-secondary/50 border-border/50" />
              </FormField>
              <button onClick={() => setEditForm(f => ({ ...f, is_vip: !f.is_vip }))}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl border text-sm font-medium transition-all
                  ${editForm.is_vip ? 'bg-warning/10 border-warning/30 text-warning' : 'bg-secondary/50 border-border/50 text-muted-foreground'}`}>
                <Star size={14} className={editForm.is_vip ? 'fill-warning' : ''} /> VIP
              </button>
            </div>

            {/* Contact d'urgence */}
            <div className="glass-card p-5 space-y-3">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Contact d'urgence</p>
              <div className="grid grid-cols-2 gap-3">
                <FormField label="Nom">
                  <Input value={editForm.ec_name} onChange={e => setEditForm(f => ({ ...f, ec_name: e.target.value }))} placeholder="Marie Dupont" className="bg-secondary/50 border-border/50" />
                </FormField>
                <FormField label="Téléphone">
                  <Input type="tel" value={editForm.ec_phone} onChange={e => setEditForm(f => ({ ...f, ec_phone: e.target.value }))} placeholder="06 00 00 00 00" className="bg-secondary/50 border-border/50" />
                </FormField>
              </div>
            </div>

            {/* Préférences */}
            <div className="glass-card p-5 space-y-3">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Préférences</p>
              <FormField label="Styles préférés">
                <div className="flex flex-wrap gap-1.5">
                  {STYLE_LIST.map(style => {
                    const selected = editForm.style_preferences.includes(style);
                    return (
                      <button key={style} type="button"
                        onClick={() => setEditForm(f => ({
                          ...f,
                          style_preferences: selected
                            ? f.style_preferences.filter(s => s !== style)
                            : [...f.style_preferences, style],
                        }))}
                        className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-all
                          ${selected ? 'bg-primary text-primary-foreground border-primary' : 'bg-secondary/50 border-border/50 text-muted-foreground hover:border-primary/30'}`}>
                        {style}
                      </button>
                    );
                  })}
                </div>
              </FormField>
              <FormField label="Notes">
                <Textarea value={editForm.notes} onChange={e => setEditForm(f => ({ ...f, notes: e.target.value }))}
                  rows={3} className="bg-secondary/50 border-border/50 resize-none" />
              </FormField>
            </div>

            {/* Santé */}
            <div className="glass-card p-5 space-y-3">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Santé</p>
              <div className="grid grid-cols-2 gap-4">
                <FormField label="Allergies">
                  <TagInput tags={editForm.allergies} onChange={v => setEditForm(f => ({ ...f, allergies: v }))} placeholder="Latex, encre rouge…" />
                </FormField>
                <FormField label="Conditions médicales">
                  <TagInput tags={editForm.conditions} onChange={v => setEditForm(f => ({ ...f, conditions: v }))} placeholder="Diabète, épilepsie…" />
                </FormField>
              </div>
              <FormField label="Médicaments">
                <Textarea value={editForm.medications} onChange={e => setEditForm(f => ({ ...f, medications: e.target.value }))}
                  rows={2} className="bg-secondary/50 border-border/50 resize-none" placeholder="Anticoagulants, isotrétinoïne…" />
              </FormField>
              <FormField label="Réactions cutanées">
                <Textarea value={editForm.skin_reactions} onChange={e => setEditForm(f => ({ ...f, skin_reactions: e.target.value }))}
                  rows={2} className="bg-secondary/50 border-border/50 resize-none" placeholder="Cicatrisation lente, chéloïdes…" />
              </FormField>
              <div className="grid grid-cols-2 gap-4">
                <FormField label="Enceinte / Allaitante">
                  <button onClick={() => setEditForm(f => ({ ...f, is_pregnant: !f.is_pregnant }))}
                    className={`w-full flex items-center gap-2 px-4 py-2.5 rounded-xl border text-sm font-medium transition-all
                      ${editForm.is_pregnant ? 'bg-warning/10 border-warning/30 text-warning' : 'bg-secondary/50 border-border/50 text-muted-foreground'}`}>
                    <Baby size={14} /> {editForm.is_pregnant ? 'Oui' : 'Non'}
                  </button>
                </FormField>
                <FormField label="Tolérance douleur (1→5)">
                  <PainDots value={editForm.pain_tolerance} onChange={v => setEditForm(f => ({ ...f, pain_tolerance: v }))} />
                </FormField>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-2">
              <Button variant="outline" className="flex-1" onClick={() => setEditMode(false)} disabled={saving}>
                Annuler
              </Button>
              <Button className="flex-1 gap-2" onClick={handleSaveEdit}
                disabled={saving || !editForm.first_name.trim()}>
                {saving ? <><Loader2 size={14} className="animate-spin" /> Enregistrement…</> : <><Check size={14} /> Enregistrer</>}
              </Button>
            </div>
          </div>
        ) : (
        <>

        {/* ── STEP 3: Tabs + 2-column layout ────────────────────────────── */}
        <div className="flex gap-2 overflow-x-auto pb-1" style={{ scrollbarWidth: 'none' }}>
          {TABS.map(tab => (
            <button key={tab} onClick={() => setActiveTab(tab)}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 shrink-0
                ${activeTab === tab
                  ? 'bg-primary text-primary-foreground shadow-md shadow-primary/25'
                  : 'bg-secondary/60 text-muted-foreground hover:bg-secondary hover:text-foreground'}`}>
              {TAB_ICONS[tab]}
              {t[tab as keyof typeof t] as string}
            </button>
          ))}
        </div>

        <div className="flex gap-6">
          {/* ── Main content ──────────────────────────────────────────────── */}
          <div className="flex-1 min-w-0">
            <AnimatePresence mode="wait">
              <motion.div key={activeTab}
                initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }} transition={{ duration: 0.15 }}>

                {/* ── STEP 5: Activité ──────────────────────────────────── */}
                {activeTab === 'activity' && (
                  <div className="space-y-4">
                    {/* Filtres timeline */}
                    <div className="flex gap-1.5 flex-wrap">
                      {([
                        { value: 'all' as const, label: 'Tout' },
                        { value: 'session' as const, label: 'Sessions' },
                        { value: 'payment' as const, label: 'Paiements' },
                        { value: 'contract' as const, label: 'Documents' },
                        { value: 'appointment' as const, label: 'RDV' },
                      ]).map(f => (
                        <button key={f.value} onClick={() => setTimelineFilter(f.value)}
                          className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all
                            ${timelineFilter === f.value ? 'bg-primary text-primary-foreground' : 'bg-secondary/60 text-muted-foreground hover:bg-secondary'}`}>
                          {f.label}
                        </button>
                      ))}
                    </div>

                    {timelineEvents.length === 0 ? (
                      <EmptyState icon={<Activity size={40} />} text="Aucune activité enregistrée" />
                    ) : (
                      <div className="relative pl-6 space-y-1">
                        {/* Ligne verticale */}
                        <div className="absolute left-2.5 top-3 bottom-3 w-px bg-border/50" />

                        {timelineEvents.map((event, i) => (
                          <motion.div key={`${event.type}-${event.id}`}
                            initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: i * 0.03 }}
                            className="relative flex items-start gap-3 py-3">
                            {/* Dot */}
                            <div className={`absolute -left-3.5 w-5 h-5 rounded-full flex items-center justify-center shrink-0 ${TIMELINE_DOT[event.type]}`}>
                              {TIMELINE_ICON[event.type]}
                            </div>
                            <div className="glass-card p-3 flex-1 min-w-0 hover:border-primary/20 transition-colors">
                              <div className="flex items-start justify-between gap-2">
                                <div className="min-w-0">
                                  <p className="text-sm font-medium text-foreground truncate">{event.title}</p>
                                  {event.description && (
                                    <p className="text-xs text-muted-foreground truncate">{event.description}</p>
                                  )}
                                </div>
                                <p className="text-xs text-muted-foreground shrink-0">
                                  {event.date ? format(new Date(event.date), 'dd MMM', { locale }) : ''}
                                </p>
                              </div>
                            </div>
                          </motion.div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* ── STEP 6: Sessions ──────────────────────────────────── */}
                {activeTab === 'sessions' && (
                  <div className="space-y-3">
                    <div className="flex justify-end">
                      <Button size="sm" onClick={() => navigate(`/sessions/new?clientId=${client.id}`)}
                        className="gap-1.5 bg-primary hover:bg-primary/90 text-primary-foreground">
                        <Plus size={14} /> {t.newSession}
                      </Button>
                    </div>
                    {sessionsLoading ? (
                      <div className="space-y-3">{[1, 2].map(i => <Skeleton key={i} className="h-24 rounded-2xl bg-secondary/50" />)}</div>
                    ) : sessions.length === 0 ? (
                      <EmptyState icon={<Clock size={40} />} text={t.noSessions} />
                    ) : (
                      sessions.map((session, i) => (
                        <motion.div key={session.id} initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.04 }}
                          className="glass-card p-4 space-y-2 hover:border-primary/20 transition-colors cursor-pointer"
                          onClick={() => navigate(`/sessions/${session.id}`)}>
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <p className="font-medium text-sm text-foreground">{session.project_name || '—'}</p>
                              <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                                <Calendar size={10} />
                                {session.started_at
                                  ? format(new Date(session.started_at), 'dd MMM yyyy', { locale })
                                  : '—'}
                                {session.ended_at && session.started_at && (
                                  <span className="ml-1">· {Math.round((new Date(session.ended_at).getTime() - new Date(session.started_at).getTime()) / 60000)} min</span>
                                )}
                              </p>
                            </div>
                            <StatusBadge status={session.status} map={t.sessionStatusMap} />
                          </div>
                          {session.notes && <p className="text-xs text-muted-foreground border-t border-border/40 pt-2">{session.notes}</p>}
                        </motion.div>
                      ))
                    )}
                  </div>
                )}

                {/* ── STEP 9: Documents (copie exacte de l'existant) ───── */}
                {activeTab === 'documents' && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex gap-1.5">
                        {(['all', 'signed', 'drafts'] as const).map(f => {
                          const labels = { all: language === 'fr' ? 'Tous' : 'All', signed: language === 'fr' ? 'Signés' : 'Signed', drafts: language === 'fr' ? 'Brouillons' : 'Drafts' };
                          return (
                            <button key={f} onClick={() => setContractFilter(f)}
                              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all
                                ${contractFilter === f ? 'bg-primary text-primary-foreground' : 'bg-secondary/60 text-muted-foreground hover:bg-secondary'}`}>
                              {labels[f]}
                            </button>
                          );
                        })}
                      </div>
                      <div className="flex items-center gap-1.5">
                        <Button size="sm" variant="outline" onClick={() => setShowSendConsent(true)} className="gap-1.5 text-muted-foreground border-border/60 hover:text-foreground">
                          <Link2 size={13} /> Lien
                        </Button>
                        <Button size="sm" onClick={() => navigate(`/templates?client_id=${id}&mode=select`)} className="gap-1.5 bg-primary hover:bg-primary/90 text-primary-foreground">
                          <Plus size={14} /> {t.newContract}
                        </Button>
                      </div>
                    </div>

                    {/* Pending counter-sign banner */}
                    {pendingContracts.length > 0 && (
                      <div className="flex items-center gap-3 px-3.5 py-2.5 bg-amber-500/10 border border-amber-500/20 rounded-xl">
                        <div className="w-2 h-2 rounded-full bg-amber-500 shrink-0" />
                        <p className="text-xs text-amber-700 dark:text-amber-400 font-medium flex-1">
                          {pendingContracts.length} consentement{pendingContracts.length > 1 ? 's' : ''} en attente de contresignature
                        </p>
                        <button
                          onClick={() => { setCounterSignId(pendingContracts[0].id); setPractitionerSig(''); }}
                          className="text-xs font-semibold text-amber-700 dark:text-amber-400 hover:underline shrink-0"
                        >
                          Voir et contresigner
                        </button>
                      </div>
                    )}

                    {contractFilter !== 'drafts' && (
                      contracts.length === 0 ? (
                        contractFilter === 'all' && (
                          <EmptyState icon={<ClipboardList size={40} />} text={t.noContracts}>
                            <Button variant="secondary" size="sm" onClick={() => navigate(`/templates?client_id=${id}&mode=select`)} className="gap-1.5 mt-3"><Plus size={14} />{t.newContract}</Button>
                          </EmptyState>
                        )
                      ) : (
                        <div className="space-y-3">
                          {contracts.map((contract, i) => (
                            <motion.div key={contract.id} initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.04 }}
                              className="glass-card p-4 flex items-center gap-3 group hover:border-primary/20 transition-colors">
                              {(contract as any).signatureDataUrl ? (
                                <img src={(contract as any).signatureDataUrl} alt="sig"
                                  className="w-14 h-10 object-contain rounded-lg bg-secondary/60 p-1 shrink-0" />
                              ) : (
                                <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                                  <FileText size={15} className="text-primary" />
                                </div>
                              )}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-1.5 flex-wrap">
                                  <p className="font-medium text-sm text-foreground truncate">{(contract as any).templateName}</p>
                                  {(contract as any).status === 'pending_practitioner' && (
                                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-700 dark:text-amber-400 font-semibold shrink-0">
                                      À contresigner
                                    </span>
                                  )}
                                </div>
                                <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                                  <Calendar size={10} /> {t.signedOn} {format(new Date((contract as any).signedAt), 'dd MMM yyyy', { locale })}
                                </p>
                              </div>
                              <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                                {(contract as any).status === 'pending_practitioner' && (
                                  <Button variant="ghost" size="sm" className="h-7 text-xs text-amber-600 hover:text-amber-700 hover:bg-amber-500/10 px-2"
                                    onClick={() => { setCounterSignId(contract.id); setPractitionerSig(''); }}>
                                    Contresigner
                                  </Button>
                                )}
                                <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-foreground"
                                  onClick={() => handlePreviewPdf(contract.id)}><Eye size={14} /></Button>
                                <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-foreground"
                                  onClick={() => handleExportPdf(contract.id)}><Download size={14} /></Button>
                                <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-foreground"
                                  onClick={() => handleShare(contract.id)}><Share2 size={14} /></Button>
                                <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive"
                                  onClick={() => setDeleteContractId(contract.id)}><Trash2 size={14} /></Button>
                              </div>
                            </motion.div>
                          ))}
                        </div>
                      )
                    )}

                    {contractFilter !== 'signed' && (
                      clientDrafts.length === 0 && contractFilter === 'drafts' ? (
                        <EmptyState icon={<FileText size={40} />} text={language === 'fr' ? 'Aucun brouillon' : 'No drafts'} />
                      ) : clientDrafts.length > 0 ? (
                        <div className="space-y-3">
                          {clientDrafts.map((draft, i) => (
                            <motion.div key={draft.id} initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.04 }}
                              className="glass-card p-4 flex items-center gap-3 hover:border-amber-500/20 transition-colors">
                              <div className="w-9 h-9 rounded-xl bg-amber-500/10 flex items-center justify-center shrink-0">
                                <FileEdit size={15} className="text-amber-600" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-1.5">
                                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-700 font-medium">
                                    {language === 'fr' ? 'Brouillon' : 'Draft'}
                                  </span>
                                  <p className="font-medium text-sm text-foreground truncate">{draft.template_name}</p>
                                </div>
                                <p className="text-xs text-muted-foreground mt-0.5">
                                  {new Date(draft.updated_at).toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', { day: 'numeric', month: 'short', year: 'numeric' })}
                                </p>
                              </div>
                              <div className="flex gap-1 shrink-0">
                                <Button size="sm" variant="outline" className="h-7 text-xs border-border/50"
                                  onClick={() => navigate(`/contract/${draft.template_id}?client_id=${id}`)}>
                                  {language === 'fr' ? 'Reprendre' : 'Resume'}
                                </Button>
                              </div>
                            </motion.div>
                          ))}
                        </div>
                      ) : null
                    )}
                  </div>
                )}

                {/* ── STEP 7: Factures ──────────────────────────────────── */}
                {activeTab === 'invoices' && (
                  <div className="space-y-3">
                    <div className="flex justify-end">
                      <Button size="sm" onClick={() => navigate(`/invoices/new?clientId=${client.id}`)}
                        className="gap-1.5 bg-primary hover:bg-primary/90 text-primary-foreground">
                        <Plus size={14} /> Nouvelle facture
                      </Button>
                    </div>
                    {clientInvoices.length === 0 ? (
                      <EmptyState icon={<Receipt size={40} />} text="Aucune facture pour ce client" />
                    ) : (
                      <ul className="space-y-2">
                        {clientInvoices.map((inv, i) => (
                          <motion.li key={inv.id} initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.04 }}
                            className="glass-card p-4 flex items-center gap-4 cursor-pointer hover:border-primary/20 transition-colors"
                            onClick={() => navigate('/invoices')}>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <p className="font-semibold text-sm">{inv.invoice_number}</p>
                                <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${
                                  inv.status === 'paid' ? 'bg-green-500/10 text-green-600 border-green-500/20'
                                  : inv.status === 'sent' ? 'bg-blue-500/10 text-blue-600 border-blue-500/20'
                                  : inv.status === 'cancelled' ? 'bg-destructive/10 text-destructive border-destructive/20'
                                  : 'bg-secondary text-muted-foreground border-border/50'}`}>
                                  {inv.status}
                                </span>
                              </div>
                              <p className="text-xs text-muted-foreground mt-0.5">
                                {inv.issue_date ? format(new Date(inv.issue_date), 'dd MMM yyyy', { locale }) : '—'}
                              </p>
                            </div>
                            <div className="text-right shrink-0">
                              <p className="font-bold tabular-nums">{(inv.total_ttc ?? 0).toFixed(2)} €</p>
                              <p className="text-xs text-muted-foreground">HT {(inv.total_ht ?? 0).toFixed(2)} €</p>
                            </div>
                          </motion.li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}

                {/* ── STEP 8: Devis ─────────────────────────────────────── */}
                {activeTab === 'quotes' && (
                  <div className="space-y-3">
                    <div className="flex justify-end">
                      <Button size="sm" onClick={() => navigate(`/quotes/new?clientId=${client.id}`)}
                        className="gap-1.5 bg-primary hover:bg-primary/90 text-primary-foreground">
                        <Plus size={14} /> Nouveau devis
                      </Button>
                    </div>
                    {clientQuotes.length === 0 ? (
                      <EmptyState icon={<FileSignature size={40} />} text="Aucun devis pour ce client" />
                    ) : (
                      <ul className="space-y-2">
                        {clientQuotes.map((q, i) => (
                          <motion.li key={q.id} initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.04 }}
                            className="glass-card p-4 flex items-center gap-4 cursor-pointer hover:border-primary/20 transition-colors"
                            onClick={() => navigate('/quotes')}>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <p className="font-semibold text-sm">{q.invoice_number}</p>
                                <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${
                                  q.status === 'accepted' ? 'bg-green-500/10 text-green-600 border-green-500/20'
                                  : q.status === 'refused' ? 'bg-destructive/10 text-destructive border-destructive/20'
                                  : q.status === 'sent' ? 'bg-blue-500/10 text-blue-600 border-blue-500/20'
                                  : 'bg-secondary text-muted-foreground border-border/50'}`}>
                                  {q.status}
                                </span>
                              </div>
                              <p className="text-xs text-muted-foreground mt-0.5">
                                {q.due_date
                                  ? `Valide jusqu'au ${format(new Date(q.due_date), 'dd MMM yyyy', { locale })}`
                                  : format(new Date(q.created_at), 'dd MMM yyyy', { locale })}
                              </p>
                            </div>
                            <div className="text-right shrink-0">
                              <p className="font-bold tabular-nums">{(q.total_ttc ?? 0).toFixed(2)} €</p>
                              {q.deposit_amount > 0 && (
                                <p className="text-xs text-muted-foreground">Acompte {q.deposit_amount.toFixed(2)} €</p>
                              )}
                            </div>
                          </motion.li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}

                {/* ── STEP 9: Photos (copie exacte de l'existant) ──────── */}
                {activeTab === 'photos' && (
                  <div className="space-y-4">
                    <div className="glass-card p-4 space-y-3">
                      <p className="text-xs text-muted-foreground font-medium">Type de photo à uploader</p>
                      <div className="flex gap-2 flex-wrap items-end">
                        <div className="flex gap-1.5">
                          {(['tattoo', 'reference', 'profile'] as const).map(type => (
                            <button key={type} onClick={() => setPhotoType(type)}
                              className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all
                                ${photoType === type ? 'bg-primary/10 border-primary/30 text-primary' : 'bg-secondary/50 border-border/50 text-muted-foreground hover:border-primary/20'}`}>
                              {t[type === 'profile' ? 'profilePic' : type]}
                            </button>
                          ))}
                        </div>
                        <Input value={photoCaption} onChange={e => setPhotoCaption(e.target.value)} placeholder={t.photoCaption} className="bg-secondary/50 border-border/50 h-8 text-sm flex-1 min-w-28" />
                        <Button size="sm" onClick={() => fileInputRef.current?.click()} disabled={uploadingPhoto} className="gap-1.5 bg-primary hover:bg-primary/90 text-primary-foreground h-8">
                          <Upload size={13} /> {t.uploadPhoto}
                        </Button>
                        <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handlePhotoUpload} />
                      </div>
                    </div>

                    {photos.length > 0 && (
                      <div className="flex gap-1.5">
                        {(['all', 'tattoo', 'reference', 'profile'] as const).map(type => {
                          const count = type === 'all' ? photos.length : photos.filter(p => p.type === type).length;
                          return (
                            <button key={type} onClick={() => setFilterType(type)}
                              className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium transition-all
                                ${filterType === type ? 'bg-primary text-primary-foreground shadow-sm shadow-primary/20' : 'bg-secondary/60 text-muted-foreground hover:bg-secondary'}`}>
                              {type === 'all' ? 'Tous' : t[type === 'profile' ? 'profilePic' : type]}
                              <span className={`text-xs px-1 rounded-full ${filterType === type ? 'bg-white/20' : 'bg-border/60'}`}>{count}</span>
                            </button>
                          );
                        })}
                      </div>
                    )}

                    {photosLoading ? (
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">{[1, 2, 3, 4].map(i => <Skeleton key={i} className="aspect-square rounded-2xl bg-secondary/50" />)}</div>
                    ) : photos.length === 0 ? (
                      <EmptyState icon={<Image size={40} />} text={t.noPhotos}>
                        <Button variant="secondary" size="sm" onClick={() => fileInputRef.current?.click()} className="gap-1.5 mt-3"><Upload size={14} />{t.uploadPhoto}</Button>
                      </EmptyState>
                    ) : (
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                        {photos
                          .filter(p => filterType === 'all' || p.type === filterType)
                          .map((photo, i) => (
                            <motion.div key={photo.id} initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: i * 0.04 }}
                              className="relative group aspect-square rounded-2xl overflow-hidden bg-secondary/50 border border-border/40">
                              <img src={photo.url} alt={photo.caption} className="w-full h-full object-cover" />
                              <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-between p-3">
                                <div className="flex justify-between items-start">
                                  <span className="text-xs px-2 py-0.5 rounded-full bg-black/40 text-white/80">{t[photo.type === 'profile' ? 'profilePic' : photo.type]}</span>
                                  <button onClick={() => deletePhoto.mutate({ id: photo.id, storage_path: photo.storage_path })}
                                    className="w-6 h-6 rounded-full bg-destructive/80 flex items-center justify-center text-white hover:bg-destructive">
                                    <X size={11} />
                                  </button>
                                </div>
                                <div className="flex gap-1 items-end">
                                  {photo.caption && <p className="text-xs text-white/80 truncate flex-1">{photo.caption}</p>}
                                  <a href={photo.url} target="_blank" rel="noreferrer" className="w-6 h-6 rounded-full bg-black/40 flex items-center justify-center text-white hover:bg-black/60 shrink-0">
                                    <ExternalLink size={10} />
                                  </a>
                                </div>
                              </div>
                            </motion.div>
                          ))}
                        {photos.filter(p => filterType === 'all' || p.type === filterType).length === 0 && (
                          <div className="col-span-3 py-8 text-center text-muted-foreground text-sm">
                            Aucune photo de ce type
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

              </motion.div>
            </AnimatePresence>
          </div>

          {/* ── STEP 4: Sidebar contextuelle (desktop) ─────────────────── */}
          <aside className="w-60 shrink-0 hidden lg:block">
            <SidebarContent />
          </aside>
        </div>
        </>
        )}
      </div>

      {/* ── Mobile sidebar bottom sheet ───────────────────────────────── */}
      <AnimatePresence>
        {sidebarOpen && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 z-40 bg-black/40" onClick={() => setSidebarOpen(false)} />
            <motion.div initial={{ y: '100%' }} animate={{ y: 0 }} exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 30, stiffness: 300 }}
              className="fixed bottom-0 inset-x-0 z-50 bg-background rounded-t-3xl border-t border-border/50 p-6 max-h-[80vh] overflow-y-auto">
              <div className="w-10 h-1 rounded-full bg-border mx-auto mb-5" />
              <SidebarContent />
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* ── PDF Preview overlay ────────────────────────────────────────── */}
      <AnimatePresence>
        {previewUrl && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-background flex flex-col">
            <div className="flex items-center justify-between px-4 py-3 border-b border-border/50 bg-card/80 backdrop-blur">
              <h2 className="font-semibold">{language === 'fr' ? 'Aperçu contrat' : 'Contract preview'}</h2>
              <Button variant="ghost" size="icon" onClick={closePreview} className="h-9 w-9 rounded-xl bg-secondary">
                <X size={18} />
              </Button>
            </div>
            <iframe src={previewUrl} className="flex-1 w-full" title="PDF Preview" />
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Delete contract confirmation ───────────────────────────────── */}
      <AnimatePresence>
        {deleteContractId && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
            onClick={() => setDeleteContractId(null)}>
            <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }}
              onClick={e => e.stopPropagation()}
              className="glass-card p-6 max-w-sm w-full space-y-4 shadow-xl border border-border/50">
              <h3 className="font-bold text-lg">{language === 'fr' ? 'Supprimer ce contrat ?' : 'Delete this contract?'}</h3>
              <p className="text-sm text-muted-foreground">{language === 'fr' ? 'Cette action est irréversible.' : 'This action cannot be undone.'}</p>
              <div className="flex gap-3">
                <Button variant="outline" className="flex-1 border-border/50" onClick={() => setDeleteContractId(null)}>{t.cancel}</Button>
                <Button className="flex-1 bg-destructive hover:bg-destructive/90 text-white" onClick={confirmDeleteContract}>{t.delete}</Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Counter-sign modal ────────────────────────────────────────── */}
      <AnimatePresence>
        {counterSignId && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
            onClick={() => setCounterSignId(null)}>
            <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }}
              onClick={e => e.stopPropagation()}
              className="bg-card border border-border rounded-2xl p-6 max-w-sm w-full space-y-4 shadow-xl">
              <h3 className="font-bold text-lg">Contresigner le consentement</h3>
              {(() => {
                const pc = pendingContracts.find(c => c.id === counterSignId);
                return pc ? (
                  <p className="text-sm font-medium text-foreground bg-secondary/60 rounded-xl px-3 py-2">
                    {pc.template_name}
                  </p>
                ) : null;
              })()}
              <p className="text-sm text-muted-foreground">
                Apposez votre signature pour valider ce consentement client.
              </p>
              <SignaturePad onSignatureChange={v => setPractitionerSig(v ?? '')} />
              <div className="flex gap-3 pt-1">
                <Button variant="outline" className="flex-1 border-border/50" onClick={() => setCounterSignId(null)}>
                  {t.cancel}
                </Button>
                <Button className="flex-1 bg-primary hover:bg-primary/90 text-primary-foreground"
                  disabled={!practitionerSig || counterSigning}
                  onClick={handleCounterSign}>
                  {counterSigning ? (
                    <Loader2 size={15} className="animate-spin mr-1.5" />
                  ) : (
                    <Check size={15} className="mr-1.5" />
                  )}
                  Valider
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Send consent link dialog ──────────────────────────────────── */}
      <SendConsentDialog
        open={showSendConsent}
        onClose={() => setShowSendConsent(false)}
        clientId={id ?? null}
        clientEmail={(client as any)?.email ?? ''}
        clientName={(client as any)?.name ?? ''}
      />
    </div>
  );
}
